/**
 * ConditionalVisibilityControls Component
 *
 * Provides UI controls for Custom Survey conditional question visibility.
 * Allows users to configure a question to only show when a specific answer
 * (or one of several answers) is selected on a previous Dropdown or Checkbox question.
 *
 * Only renders when there is at least one preceding Dropdown/Checkbox question.
 */

import { FormFieldType, IFormField } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../../components-v2/Button';
import { Checkbox } from '../../../../../../components-v2/Checkbox';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../../../../components-v2/Combobox';
import { XIcon } from '../../../../../../components/svgs/icons/XIcon';
import { bs } from '../../../../../../styles/styles';
import { usePreviewMode } from '../../../contexts/PreviewModeContext';
import { styleSheet } from '../styles';
import { ConditionalVisibilityProps } from '../types';

/**
 * Derives the list of preceding fields that are Dropdown or Checkbox type
 */
const getPrecedingOptionFields = (allFields: IFormField<string>[], fieldIndex: number) => {
	return allFields
		.slice(0, fieldIndex)
		.reduce<{ field: IFormField<string>; originalIndex: number }[]>((acc, field, idx) => {
			if (field.fieldType === FormFieldType.Option || field.fieldType === FormFieldType.MultipleOptions) {
				acc.push({ field, originalIndex: idx });
			}
			return acc;
		}, []);
};

interface QuestionOption {
	title: string;
	value: string;
}

interface AnswerOption {
	title: string;
	value: string;
}

/**
 * Builds question options from preceding option-type fields
 */
const buildQuestionOptions = (
	precedingFields: { field: IFormField<string>; originalIndex: number }[]
): QuestionOption[] => {
	return precedingFields.map(({ field, originalIndex }) => ({
		title: `Question ${originalIndex + 1}`,
		value: field.id,
	}));
};

/**
 * Set of option ids currently picked by ANY OTHER answer row (excludes the
 * current row's own selection). Used to disable already-used options in each
 * row's dropdown so the same answer can't be added twice.
 */
const computeUsedByOtherRows = (selectedOptionIds: string[] | undefined, currentRowIndex: number): Set<string> => {
	if (!selectedOptionIds) return new Set();
	const result = new Set<string>();
	selectedOptionIds.forEach((id, idx) => {
		if (idx !== currentRowIndex && id) result.add(id);
	});
	return result;
};

/**
 * Builds answer options from a field's options
 */
const buildAnswerOptions = (allFields: IFormField<string>[], selectedQuestionId?: string): AnswerOption[] => {
	if (!selectedQuestionId) return [];
	const selectedField = allFields.find(f => f.id === selectedQuestionId);
	if (!selectedField?.options) return [];
	return selectedField.options
		.filter(opt => opt.label?.trim())
		.map(opt => ({
			title: opt.label!,
			value: opt.id!,
		}));
};

/**
 * Controls for managing conditional question visibility based on a previous question's answer
 */
export const ConditionalVisibilityControls: React.FC<ConditionalVisibilityProps> = ({
	field,
	fieldIndex,
	allFields,
	state,
	onToggle,
	onQuestionSelect,
	onAnswerSelect,
	onAnswerAdd,
	onAnswerRemove,
}) => {
	const { isPreviewOnly } = usePreviewMode();

	const isEnabled = state?.enabled ?? false;
	const selectedQuestionId = state?.dependsOnFieldId;
	const selectedOptionIds = state?.dependsOnOptionIds;

	const precedingOptionFields = React.useMemo(
		() => getPrecedingOptionFields(allFields, fieldIndex),
		[allFields, fieldIndex]
	);

	const questionOptions = React.useMemo(() => buildQuestionOptions(precedingOptionFields), [precedingOptionFields]);

	const selectedQuestionOption = React.useMemo(
		() => questionOptions.find(opt => opt.value === selectedQuestionId) ?? null,
		[questionOptions, selectedQuestionId]
	);

	const answerOptions = React.useMemo(
		() => buildAnswerOptions(allFields, selectedQuestionId),
		[allFields, selectedQuestionId]
	);

	// Lookup the source field so we can branch on its type when deciding the
	// max number of answer rows allowed (see `maxAnswerRows` below).
	const selectedSourceField = React.useMemo(
		() => (selectedQuestionId ? allFields.find(f => f.id === selectedQuestionId) : undefined),
		[allFields, selectedQuestionId]
	);

	/**
	 * Cap on how many answer rows the rule may have: the number of options.
	 * Dropdown (Option) and Checkbox (MultipleOptions) sources both allow up to
	 * `answerOptions.length` — a rule may reference every option.
	 *
	 * Deps read the primitive `fieldType` (not the `selectedSourceField` object
	 * reference) so the memo recomputes whenever the source-question type flips.
	 */
	const selectedSourceFieldType = selectedSourceField?.fieldType;
	const maxAnswerRows = React.useMemo(() => {
		if (!selectedSourceFieldType) return 0;
		return answerOptions.length;
	}, [selectedSourceFieldType, answerOptions.length]);

	const handleCheckboxChange = React.useCallback(
		(checked: boolean | 'indeterminate') => {
			onToggle(field.id, Boolean(checked));
		},
		[field.id, onToggle]
	);

	const handleQuestionChange = React.useCallback(
		(value: string) => {
			onQuestionSelect(field.id, value);
		},
		[field.id, onQuestionSelect]
	);

	const handleAddAnswer = React.useCallback(() => {
		onAnswerAdd(field.id);
	}, [field.id, onAnswerAdd]);

	const isQuestionSelectDisabled = !isEnabled || isPreviewOnly;
	const isAnswerSelectDisabled = !isEnabled || !selectedQuestionId || isPreviewOnly;

	// Don't render if no preceding dropdown/checkbox questions exist
	if (precedingOptionFields.length === 0) {
		return null;
	}

	// Determine answer rows to display
	const answerCount = selectedOptionIds?.length ?? 0;
	const hasFirstAnswer = answerCount > 0;
	const additionalAnswerIndices = hasFirstAnswer ? selectedOptionIds!.slice(1).map((_, i) => i + 1) : [];

	return (
		<div className={css(styleSheet.conditionalVisibilityContainer)}>
			<div className={css(styleSheet.conditionalVisibilityRow)}>
				<div className={css(styleSheet.conditionalVisibilityTriggerGroup)}>
					<Checkbox
						checked={isEnabled}
						id={`conditional-visibility-${fieldIndex}-checkbox`}
						onCheckedChange={handleCheckboxChange}
						disabled={isPreviewOnly}
					/>
					<span>Only show when</span>
					{/*
					 * Source-question picker. Item values are the QuestionOption objects
					 * themselves so typeahead filtering + the trigger display run off the
					 * human title (`itemToStringLabel`) rather than the fieldId UUID.
					 * `isItemEqualToValue` matches by `value` so the controlled value
					 * resolves regardless of object identity, and `onValueChange` funnels
					 * the chosen fieldId back through the existing `onQuestionSelect` callback.
					 */}
					<div className={css(styleSheet.conditionalVisibilityQuestionDropdown)}>
						<Combobox
							items={questionOptions}
							value={selectedQuestionOption}
							onValueChange={value => {
								const opt = value as QuestionOption | null;
								if (opt) handleQuestionChange(opt.value);
							}}
							itemToStringLabel={value => (value as QuestionOption).title}
							isItemEqualToValue={(item, value) =>
								(item as QuestionOption)?.value === (value as QuestionOption | null)?.value
							}
							disabled={isQuestionSelectDisabled}
						>
							<ComboboxInput
								placeholder='Select question'
								aria-label='Select question'
								disabled={isQuestionSelectDisabled}
								styles={[styleSheet.sourceComboboxInput]}
							/>
							<ComboboxContent>
								<ComboboxEmpty>No questions found</ComboboxEmpty>
								<ComboboxList>
									{(opt: QuestionOption) => (
										<ComboboxItem key={opt.value} value={opt}>
											<ComboboxItemRadioIndicator />
											<span className={css(bs.truncateText, styleSheet.comboboxItemLabel)}>{opt.title}</span>
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>
					<span>is</span>
				</div>
				<div className={css(styleSheet.conditionalVisibilityAnswersColumn)}>
					<AnswerRow
						answerOptions={answerOptions}
						selectedOptionId={hasFirstAnswer ? selectedOptionIds![0] : undefined}
						usedByOtherRows={computeUsedByOtherRows(selectedOptionIds, 0)}
						answerIndex={0}
						fieldId={field.id}
						disabled={isAnswerSelectDisabled}
						isPreviewOnly={isPreviewOnly}
						onAnswerSelect={onAnswerSelect}
					/>
					{additionalAnswerIndices.map(answerIdx => (
						<div key={`or-row-${answerIdx}`} className={css(styleSheet.conditionalVisibilityOrRow)}>
							<span className={css(styleSheet.conditionalVisibilityOrLabel)}>-or-</span>
							<AnswerRow
								answerOptions={answerOptions}
								selectedOptionId={selectedOptionIds![answerIdx]}
								usedByOtherRows={computeUsedByOtherRows(selectedOptionIds, answerIdx)}
								answerIndex={answerIdx}
								fieldId={field.id}
								disabled={isAnswerSelectDisabled}
								isPreviewOnly={isPreviewOnly}
								onAnswerSelect={onAnswerSelect}
								showRemove
								onRemove={() => onAnswerRemove(field.id, answerIdx)}
							/>
						</div>
					))}
					{/*
					 * "+ Add Answer" hides when the rule has reached the cap: both Dropdown
					 * and Checkbox sources allow up to `options.length` answer rows (one per
					 * option). Always hidden in preview mode.
					 */}
					{isEnabled && !!selectedQuestionId && !isPreviewOnly && answerCount < maxAnswerRows && (
						<Button
							type='button'
							variant='link'
							onClick={handleAddAnswer}
							styles={[styleSheet.conditionalVisibilityAddAnswerButton]}
						>
							+ Add Answer
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

/**
 * Single answer row with a Combobox and optional remove (X) button
 */
interface AnswerRowProps {
	answerOptions: AnswerOption[];
	selectedOptionId?: string;
	/**
	 * Option ids already selected by OTHER answer rows on this rule. Those
	 * options are rendered disabled in this row's dropdown so the user can't
	 * pick the same answer twice. The row's own selection stays enabled.
	 */
	usedByOtherRows: Set<string>;
	answerIndex: number;
	fieldId: string;
	disabled: boolean;
	isPreviewOnly: boolean;
	onAnswerSelect: (fieldId: string, answerIndex: number, optionId: string) => void;
	showRemove?: boolean;
	onRemove?: () => void;
}

const AnswerRow: React.FC<AnswerRowProps> = React.memo(
	({
		answerOptions,
		selectedOptionId,
		usedByOtherRows,
		answerIndex,
		fieldId,
		disabled,
		isPreviewOnly,
		onAnswerSelect,
		showRemove,
		onRemove,
	}) => {
		const handleAnswerChange = React.useCallback(
			(value: string) => {
				onAnswerSelect(fieldId, answerIndex, value);
			},
			[fieldId, answerIndex, onAnswerSelect]
		);

		// Augment options with their per-row disabled state so it's data-visible to
		// both Base UI (item disabling) and the controlled value lookup. The Combobox
		// `value` is the option object itself so typeahead + the trigger display run
		// off the title (`itemToStringLabel`) rather than the raw option id.
		const items = React.useMemo(
			() => answerOptions.map(opt => ({ ...opt, disabled: usedByOtherRows.has(opt.value) })),
			[answerOptions, usedByOtherRows]
		);

		const selectedItem = React.useMemo(
			() => items.find(opt => opt.value === selectedOptionId) ?? null,
			[items, selectedOptionId]
		);

		return (
			<div className={css(styleSheet.conditionalVisibilityAnswerRow)}>
				<div className={css(styleSheet.conditionalVisibilityAnswerDropdown)}>
					<Combobox
						items={items}
						value={selectedItem}
						onValueChange={value => {
							const opt = value as AnswerOption | null;
							if (opt) handleAnswerChange(opt.value);
						}}
						itemToStringLabel={value => (value as AnswerOption).title}
						isItemEqualToValue={(item, value) =>
							(item as AnswerOption)?.value === (value as AnswerOption | null)?.value
						}
						disabled={disabled}
					>
						<ComboboxInput
							placeholder='Select answer'
							aria-label='Select answer'
							disabled={disabled}
							styles={[styleSheet.sourceComboboxInput]}
						/>
						<ComboboxContent>
							<ComboboxEmpty>No answers found</ComboboxEmpty>
							<ComboboxList>
								{(opt: AnswerOption & { disabled: boolean }) => (
									<ComboboxItem key={opt.value} value={opt} disabled={opt.disabled}>
										<ComboboxItemRadioIndicator />
										<span className={css(bs.truncateText, styleSheet.comboboxItemLabel)}>{opt.title}</span>
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
				{showRemove && !isPreviewOnly && (
					<Button
						type='button'
						variant='ghost'
						size='icon'
						onClick={onRemove}
						aria-label={`Remove answer ${answerIndex + 1}`}
						title='Remove this answer'
						styles={[styleSheet.answerRemoveButton]}
					>
						<XIcon width={16} height={16} />
					</Button>
				)}
			</div>
		);
	}
);

AnswerRow.displayName = 'AnswerRow';
